import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FiWifi, FiWifiOff, FiSave, FiAlertTriangle } from 'react-icons/fi';
import useMqttAdminConnection from '../../../hooks/useMqttAdminConnection';
import { useAgentAuthorization } from '../../../hooks/useAgentAuthorization';

const MqttAdminControls = ({ agent, onRefreshAgent }) => {
  const { canEditAgent } = useAgentAuthorization();
  const {
    mqttConnection,
    isConnecting,
    connectionError,
    pendingChanges,
    applyConfigToAgent,
    sendCommandToAgent,
    syncChangesToBackend,
    syncAllChangesToBackend,
    canUseMqttAdmin,
  } = useMqttAdminConnection();

  const [isApplying, setIsApplying] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const agentPendingChanges = pendingChanges.filter(change => change.agentId === agent.agentId);
  const hasPendingChanges = agentPendingChanges.length > 0;
  const hasEditPermission = canEditAgent(agent);

  const enableDisableBtnClass = `px-3 py-1 text-white rounded disabled:bg-gray-400 ${
    agent.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
  }`;

  const handleApplyConfig = async config => {
    if (!mqttConnection) {
      toast.error('MQTT connection not available');
      return;
    }
    setIsApplying(true);
    try {
      await applyConfigToAgent(agent.agentId, config);
      toast.success('Configuration applied to agent via MQTT');
    } catch (error) {
      toast.error(`Failed to apply configuration: ${error.message}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleSendCommand = async (action, parameters = {}) => {
    if (!mqttConnection) {
      toast.error('MQTT connection not available');
      return;
    }
    setIsApplying(true);
    try {
      await sendCommandToAgent(agent.agentId, action, parameters);
      toast.success(`Command "${action}" sent to agent`);
    } catch (error) {
      toast.error(`Failed to send command: ${error.message}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleSyncAll = async () => {
    if (!hasPendingChanges) {
      toast.info('No pending changes to sync');
      return;
    }
    setIsSyncing(true);
    try {
      await syncAllChangesToBackend();
      onRefreshAgent();
    } catch (error) {
      toast.error(`Failed to sync changes: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncChange = async actionId => {
    setIsSyncing(true);
    try {
      await syncChangesToBackend(actionId);
      onRefreshAgent();
    } catch (error) {
      toast.error(`Failed to sync change: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  if (!canUseMqttAdmin || !hasEditPermission) {
    return null;
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center">
        <h3 className="text-lg font-semibold flex items-center">
          {mqttConnection ? (
            <FiWifi className="text-green-500 mr-2" />
          ) : (
            <FiWifiOff className="text-gray-500 mr-2" />
          )}
          MQTT Admin Controls
        </h3>
      </div>

      <div className="mt-4">
        <div className="mb-4">
          <div className="flex items-center">
            <span className="font-medium mr-2">Connection Status:</span>
            {isConnecting ? (
              <span className="text-blue-500">Connecting...</span>
            ) : mqttConnection ? (
              <span className="text-green-500">Connected</span>
            ) : (
              <span className="text-red-500">Disconnected</span>
            )}
          </div>
          {connectionError && <div className="text-red-500 text-sm mt-1">{connectionError}</div>}
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Quick Actions</h4>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleSendCommand('ping')}
              disabled={!mqttConnection || isApplying}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              Check status
            </button>
            <button
              onClick={() => handleSendCommand('restart')}
              disabled={!mqttConnection || isApplying}
              className="px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-gray-400"
            >
              Restart Agent
            </button>
            <button
              onClick={() => handleSendCommand('network-scan')}
              disabled={!mqttConnection || isApplying}
              className="px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:bg-gray-400"
            >
              Network Scan
            </button>
            <button
              onClick={() => handleSendCommand('diagnostics')}
              disabled={!mqttConnection || isApplying}
              className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
            >
              Run Diagnostics
            </button>
            <button
              onClick={() => handleSendCommand(agent.enabled ? 'set_disabled' : 'set_enabled')}
              disabled={!mqttConnection || isApplying}
              className={enableDisableBtnClass}
            >
              {agent.enabled ? 'Disable Agent' : 'Enable Agent'}
            </button>
          </div>
        </div>

        {hasPendingChanges && (
          <div className="mb-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center">
                <FiAlertTriangle className="text-amber-500 mr-1" />
                Pending Changes
              </h4>
              <button
                onClick={handleSyncAll}
                disabled={isSyncing}
                className="flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                <FiSave className="mr-1" />
                Save All to System
              </button>
            </div>
            <div className="mt-2 text-sm text-amber-700">
              These changes have been applied to the agent via MQTT but have not been saved to the
              backend. Click "Save to System" to persist these changes.
            </div>
            <ul className="mt-2 divide-y">
              {agentPendingChanges.map(change => (
                <li key={change.actionId} className="py-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">
                        {change.type === 'config'
                          ? 'Configuration Change'
                          : `Command: ${change.changes.action}`}
                      </span>
                      <div className="text-xs text-gray-500">
                        {new Date(change.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleSyncChange(change.actionId)}
                      disabled={isSyncing}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                    >
                      Save to System
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

MqttAdminControls.propTypes = {
  agent: PropTypes.object.isRequired,
  onRefreshAgent: PropTypes.func.isRequired,
};

export default MqttAdminControls;
